import React, { useState, useEffect, useCallback, useRef } from 'react';
import Typography from 'apollo-react/components/Typography';
import Grid from 'apollo-react/components/Grid';
import Box from 'apollo-react/components/Box';
import axiosInstance from '../../../../../axiosInstance';
import CommentsList from '../../../../common/FormApproveComponents/CommentsList/CommentsList';
import CommentToggle from '../../../../common/CommentToggle/CommentToggle';
import ApproveButton from '../../../../common/FormApproveComponents/ApproveButton/ApproveButton';
import { isReviewerActionableStatus } from '../../../../../constants/statuses';
import ViewOnlyFileDisplay from '../../../../common/ViewOnlyFileDisplay';

const formatDisplayDate = (d) => {
  if (!d) return '-';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? String(d) : x.toLocaleDateString('en-GB');
};

const HcpLicensesListView = React.memo(
  ({
    hcpLicensesListData = null,
    persona = 'enduser',
    isSendbackMode = false,
    role = '',
    requestProgressStatus = '',
    requestProgress = null,
    isApproved = false,
    isApproving = false,
    isDeleting = false,
    onApprove = null,
    onUndo = null,
    onCommentAdded = null,
  }) => {
    const [comments, setComments] = useState([]);

    const hcpLicensesList = hcpLicensesListData;
    const isInternal = persona === 'admin';

    const onApproveRef = useRef(onApprove);
    const onUndoRef = useRef(onUndo);

    useEffect(() => {
      onApproveRef.current = onApprove;
      onUndoRef.current = onUndo;
    }, [onApprove, onUndo]);

    const handleApprove = useCallback(() => {
      if (onApproveRef.current) {
        onApproveRef.current();
      }
    }, []);

    const handleUndo = useCallback(() => {
      if (onUndoRef.current) {
        onUndoRef.current();
      }
    }, []);

    const fetchComments = useCallback(async () => {
      if (!hcpLicensesList?.id) return;
      try {
        const endpoint = isInternal
          ? `/api/hcp-licenses-list-comments/${hcpLicensesList?.id}`
          : `/api/hcp-licenses-list-comments/enduser/${hcpLicensesList?.id}`;
        const response = await axiosInstance.get(endpoint);
        setComments(response.data || []);
      } catch (error) {
        console.error('error fetching comments:', error);
      }
    }, [hcpLicensesList?.id, isInternal]);

    useEffect(() => {
      if (hcpLicensesList?.id) {
        fetchComments();
      }
    }, [hcpLicensesList?.id, fetchComments]);

    const handleAddComment = async (
      comment,
      _subFormId,
      fileId = null,
      fileData = null
    ) => {
      try {
        const requestData = {
          hcpLicensesListId: hcpLicensesList?.id,
          comment,
        };

        if (fileId) {
          requestData.additional_file_id = fileId;
        }

        const endpoint = isInternal
          ? `/api/hcp-licenses-list-comments`
          : `/api/hcp-licenses-list-comments/enduser`;
        const response = await axiosInstance.post(endpoint, requestData);

        const enrichedComment = { ...response.data, isOwner: true };
        if (fileData && fileId) {
          enrichedComment.additional_file = {
            id: fileData.id,
            original_name: fileData.original_name,
            file_path: null,
            file_size: fileData.file_size,
            mime_type: fileData.mime_type,
            createdAt: fileData.created_at,
            updatedAt: fileData.created_at,
            deletedAt: null,
            user_id: null,
            end_user_id: null,
            ...fileData,
          };
        }

        setComments((prev) => [enrichedComment, ...prev]);

        if (onCommentAdded) {
          onCommentAdded();
        }
      } catch (error) {
        console.error('error adding comment:', error);
        throw error;
      }
    };

    const handleEditComment = async (
      commentId,
      newText,
      attachmentInfo = {}
    ) => {
      try {
        const requestData = { comment: newText };

        if (attachmentInfo.remove_attachment) {
          requestData.remove_attachment = true;
        } else if (attachmentInfo.new_file_id) {
          requestData.additional_file_id = attachmentInfo.new_file_id;
        }

        const endpoint = isInternal
          ? `/api/hcp-licenses-list-comments/${commentId}`
          : `/api/hcp-licenses-list-comments/enduser/${commentId}`;

        await axiosInstance.put(endpoint, requestData);
        fetchComments();
      } catch (error) {
        console.error('error editing comment:', error);
        throw error;
      }
    };

    const handleDeleteComment = async (commentId) => {
      try {
        const endpoint = isInternal
          ? `/api/hcp-licenses-list-comments/${commentId}`
          : `/api/hcp-licenses-list-comments/enduser/${commentId}`;

        await axiosInstance.delete(endpoint);
        setComments((prev) => prev.filter((c) => c.id !== commentId));
      } catch (error) {
        console.error('error deleting comment:', error);
        throw error;
      }
    };

    if (!hcpLicensesList) {
      return (
        <>
          <Typography variant="body1">
            No License Information data available for this request.
          </Typography>
        </>
      );
    }

    const additionalFiles = Array.isArray(
      hcpLicensesList.additionalAttachmentFiles
    )
      ? hcpLicensesList.additionalAttachmentFiles
      : [];

    return (
      <>
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: '#333',
              paddingBottom: 1,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            License Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                HCP Name
              </Typography>
              <Typography variant="body1">
                {hcpLicensesList.hcp_name || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                HCP Emirates ID #
              </Typography>
              <Typography variant="body1">
                {hcpLicensesList.hcp_emirates_id || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                HCP Email
              </Typography>
              <Typography variant="body1">
                {hcpLicensesList.hcp_email || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                HCP Phone #
              </Typography>
              <Typography variant="body1">
                {hcpLicensesList.hcp_phone || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                License #
              </Typography>
              <Typography variant="body1">
                {hcpLicensesList.license_number || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                License Issue Date
              </Typography>
              <Typography variant="body1">
                {formatDisplayDate(hcpLicensesList.license_issue_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                License Renewal/End Date
              </Typography>
              <Typography variant="body1">
                {formatDisplayDate(hcpLicensesList.license_end_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                Total CME Hours
              </Typography>
              <Typography variant="body1">
                {hcpLicensesList.total_cme_hours != null &&
                hcpLicensesList.total_cme_hours !== ''
                  ? String(hcpLicensesList.total_cme_hours)
                  : '-'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: '#333',
              paddingBottom: 1,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            Documents
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                Attach License Image
              </Typography>
              {hcpLicensesList.licenseImage ? (
                <ViewOnlyFileDisplay
                  files={[
                    {
                      id: hcpLicensesList.licenseImage.id,
                      file_id: hcpLicensesList.licenseImage.id,
                      name: hcpLicensesList.licenseImage.original_name,
                      file_name: hcpLicensesList.licenseImage.original_name,
                      file_size: hcpLicensesList.licenseImage.file_size,
                      mime_type: hcpLicensesList.licenseImage.mime_type,
                    },
                  ]}
                  label=""
                  helperText="Download license image"
                  persona={persona}
                />
              ) : (
                <Typography variant="body1">-</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                Additional Attachments
              </Typography>
              {additionalFiles.length > 0 ? (
                <ViewOnlyFileDisplay
                  files={additionalFiles.map((f) => ({
                    id: f.id,
                    file_id: f.id,
                    name: f.original_name,
                    file_name: f.original_name,
                    file_size: f.file_size,
                    mime_type: f.mime_type,
                  }))}
                  label=""
                  helperText="Download attachment"
                  persona={persona}
                />
              ) : (
                <Typography variant="body1">-</Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        <div
          className="form-comment-section"
          style={{
            isolation: 'isolate',
            position: 'relative',
            zIndex: 1,
            marginTop: '35px',
          }}
        >
          <CommentsList
            prevComments={comments}
            pagination={{}}
            filters={{}}
            updateFilters={() => {}}
            persona={isInternal ? 'admin' : role || 'enduser'}
            showControls={false}
            showFilters={false}
            showSorting={false}
            showPageSize={false}
            showResultsInfo={false}
            showPagination={false}
            onEdit={handleEditComment}
            onDelete={handleDeleteComment}
          />

          {isReviewerActionableStatus(requestProgressStatus) && isInternal && (
            <CommentToggle
              onAddComment={handleAddComment}
              persona="admin"
              buttonText="Add Comment"
              buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
              hasComments={comments && comments.length > 0}
            />
          )}

          {isSendbackMode && (
            <CommentToggle
              onAddComment={handleAddComment}
              persona="enduser"
              buttonText="Add Comment"
              buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
              hasComments={comments && comments.length > 0}
            />
          )}

          {onApprove && onUndo && (
            <Box
              sx={{
                marginTop: 2,
                marginBottom: 2,
                fontSize: '16px',
                '& button': {
                  fontSize: '16px !important',
                  fontWeight: '600 !important',
                },
              }}
            >
              <ApproveButton
                onApprove={handleApprove}
                onUndo={handleUndo}
                isApproving={isApproving}
                isDeleting={isDeleting}
                isApproved={isApproved}
                requestProgress={requestProgress}
              >
                {hcpLicensesList?.id ? [hcpLicensesList.id] : []}
              </ApproveButton>
            </Box>
          )}
        </div>
      </>
    );
  },
  (prevProps, nextProps) => {
    if (
      prevProps.hcpLicensesListData?.id !== nextProps.hcpLicensesListData?.id
    ) {
      return false;
    }
    if (
      prevProps.hcpLicensesListData?.license_end_date !==
      nextProps.hcpLicensesListData?.license_end_date
    ) {
      return false;
    }
    if (prevProps.isApproved !== nextProps.isApproved) {
      return false;
    }
    if (
      prevProps.isApproving !== nextProps.isApproving ||
      prevProps.isDeleting !== nextProps.isDeleting
    ) {
      return false;
    }
    if (
      prevProps.persona !== nextProps.persona ||
      prevProps.role !== nextProps.role ||
      prevProps.isSendbackMode !== nextProps.isSendbackMode
    ) {
      return false;
    }
    return true;
  }
);

HcpLicensesListView.displayName = 'HcpLicensesListView';

export default HcpLicensesListView;
